import React, { useEffect, useRef, useState, type FC } from 'react';
import { Button, Link, Space, Switchbox, Text } from '@g2u/ui-kit';
import { Card, Col, Form, Popconfirm, Row } from 'antd';
import { PlusCircleOutlined, RedoOutlined } from '@ant-design/icons';
import { useWatch } from 'antd/es/form/Form';
import he from 'he';

import { type JsonDataItem, type WithOptionalDataTest } from 'types';
import { CreateMessageModal, TaskNotesAndLogs } from 'components';
import { AddNotePopconfirm, TicketActions, TicketHardwareReturn } from '.';
import { PutTicketParams } from 'queries';
import { useTicketDetails } from '../hooks';
import { validateFormSilently } from 'helpers';
import styles from '../tickets.module.scss';

export namespace TicketDetails {
  export type Props = {
    record: JsonDataItem;
    onClose: (args: { taskId: number; noteTypeId: number; note: string }) => void;
    onReopen: (args: { taskId: number }) => void;
    onLock: (args: { taskId: number }) => void;
    onUnlock: (args: { taskId: number }) => void;
    onCallToCustomer: () => void;
    onUpdateTicket: (payload: PutTicketParams) => void;
    hasLogs: boolean;
  } & WithOptionalDataTest;

  export const $: FC<Props> = (props) => {
    const { record, onClose, onLock, onUnlock, onReopen, hasLogs, onCallToCustomer, onUpdateTicket } = props;
    const [form] = Form.useForm();
    const formValues = useWatch([], form);
    const { lockData, isDetailsLoading, logs, notes, noteTypes, addNote } = useTicketDetails(
      record.ID,
      !record.archive_datetime,
      formValues?.showLogs,
    );
    const isRemoveLockAvailable = useRef<boolean>(true);
    const [isAddNoteFormValid, setIsAddNoteFormValid] = useState<boolean>(false);
    const isTicketClosed = !!record.archive_datetime;
    const isHardwareReturnTicket = record.record.type === 3; // hardware return type ID

    const [isCreateEmailOpen, setIsCreateEmailOpen] = useState(false);
    const [isCreateSmsOpen, setIsCreateSmsOpen] = useState(false);

    useEffect(() => {
      validateFormSilently(form, setIsAddNoteFormValid);
    }, [formValues]);

    useEffect(() => {
      if (!isTicketClosed) {
        onLock({ taskId: record.ID });
      }
      return () => {
        isRemoveLockAvailable.current && !isTicketClosed && onUnlock({ taskId: record.ID });
      };
    }, []);

    const handleCloseAction = (args: { taskId: number; noteTypeId: number; note: string }) => {
      isRemoveLockAvailable.current = false;
      onClose(args);
    };

    return (
      <div className={styles.tickets__details}>
        <div className={styles.tickets__detailsColumn}>
          {isHardwareReturnTicket && (
            <TicketHardwareReturn.$
              technicianId={record.record.technician_ID}
              ticketId={record.record_ID}
              date={record?.record?.eta_date}
              onUpdate={onUpdateTicket}
            />
          )}
          <Card className={styles.tickets__detailsSection}>
            <Text.$ style={{ whiteSpace: 'pre-line' }}>{he.decode(record.record?.description || '')}</Text.$>
          </Card>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24}>
              {!isTicketClosed ? (
                <TicketActions.$
                  isLoading={isDetailsLoading}
                  lockedBy={lockData?.data[record.ID]?.hard_lock ? lockData?.data[record.ID].user.name : undefined}
                  taskId={record.ID}
                  noteTypes={noteTypes}
                  onClose={handleCloseAction}
                  onSms={() => setIsCreateSmsOpen(true)}
                  onEmail={() => setIsCreateEmailOpen(true)}
                  onCall={onCallToCustomer}
                />
              ) : (
                <Space.$>
                  <Popconfirm
                    title="Reopen Ticket"
                    onConfirm={() => {
                      onReopen({ taskId: record.ID });
                    }}
                    description="Are you sure you want to reopen this task?"
                    okText="Yes"
                  >
                    <div>
                      <Button.$ dataTestId="reopen" onClick={() => null} loading={isDetailsLoading}>
                        <RedoOutlined /> Reopen
                      </Button.$>
                    </div>
                  </Popconfirm>
                </Space.$>
              )}
            </Col>
          </Row>
        </div>
        <Form form={form} layout="vertical" colon={false} className={styles.tickets__detailsColumn}>
          <Card
            actions={[
              !isTicketClosed && (
                <AddNotePopconfirm.$
                  title="Add New Note"
                  key="add"
                  form={form}
                  noteTypes={noteTypes}
                  isFormValid={isAddNoteFormValid}
                  onConfirm={() => {
                    addNote({
                      noteTypeId: form.getFieldValue('noteType'),
                      note: form.getFieldValue('newNote'),
                      taskId: record.ID,
                    });
                  }}
                >
                  <Link.$ className={styles.tickets__action} disabled={isTicketClosed}>
                    <PlusCircleOutlined className={styles.tickets__actionIcon} /> New Note
                  </Link.$>
                </AddNotePopconfirm.$>
              ),

              <Space.$ direction={Space.Direction.Horizontal} justify={Space.Justify.Center} key="logs-switch">
                <Switchbox.$ name="showSystemNotes" size="small" label="System Notes" colon={false} horizontal />
              </Space.$>,

              hasLogs && (
                <Space.$ direction={Space.Direction.Horizontal} justify={Space.Justify.Center} key="logs-toggle">
                  <Switchbox.$
                    name="showLogs"
                    size="small"
                    label={<Text.$>Show Logs</Text.$>}
                    colon={false}
                    horizontal
                    onChange={(v) => v && form.setFieldValue('showSystemNotes', false)}
                  />
                </Space.$>
              ),
            ].filter(Boolean)}
          >
            <TaskNotesAndLogs.$
              noteLabels={noteTypes ? Object.fromEntries(noteTypes.map((item) => [item.ID, item.desc])) : undefined}
              notes={formValues?.showSystemNotes ? notes : notes?.filter((note: JsonDataItem) => note.type !== 0)}
              logs={logs}
              isLoading={isDetailsLoading}
              showLogs={formValues?.showLogs}
            />
          </Card>
        </Form>
        <CreateMessageModal.$
          messageType="email"
          isOpen={isCreateEmailOpen}
          onClose={() => setIsCreateEmailOpen(false)}
          subtitle={`Ticket ${record?.record?.ID}`}
          entityType="task"
          id={record.ID}
          customerTo={{
            destination: record?.task_links?.customer?.email,
            name: record?.task_links?.customer?.name?.full_name,
          }}
          technicianTo={{
            destination: record?.task_links?.technician?.email,
            name: record?.task_links?.technician?.name,
          }}
        />
        <CreateMessageModal.$
          messageType="sms"
          isOpen={isCreateSmsOpen}
          onClose={() => setIsCreateSmsOpen(false)}
          subtitle={`Ticket ${record?.record?.ID}`}
          id={record.ID}
          entityType="task"
          customerTo={{ destination: record?.task_links?.customer?.mobile[0] }}
          technicianTo={{ destination: record?.task_links?.technician?.mobile[0] }}
        />
      </div>
    );
  };
}
